import axios from 'axios'
import Vue from 'vue'

export default {
  namespaced: true,
  state: {
    workspace: [],
    folders: []
  },

  mutations: {
    setWorkspace (state, workspace) {
      state.workspace = workspace
    },

    addFile (state, file) {
      state.workspace.push(file)
    },

    updateFile (state, file) {
      const fileIndex = state.workspace.findIndex(workspaceFile => workspaceFile.id === file.id)

      if (fileIndex >= 0) {
        const updatedFile = state.workspace[fileIndex]

        updatedFile.queryName = file.queryName
        updatedFile.queryText = file.queryText
        Vue.set(state.workspace, fileIndex, updatedFile)
      }
    },

    removeFile (state, fileId) {
      const fileIndex = state.workspace.findIndex(workspaceFile => workspaceFile.id === fileId)

      if (fileIndex >= 0) {
        state.workspace.splice(fileIndex, 1)
      }
    },

    addFolder (state, { folderName, folderPath }) {
      state.folders.push({
        name: folderName,
        path: folderPath
      })
    },

    removeFolder (state, { folderName, folderPath }) {
      const folderIndex = state.folders.findIndex(folder => folder.name === folderName && folder.path === folderPath)

      if (folderIndex >= 0) {
        state.folders.splice(folderIndex, 1)
      }
    }
  },

  actions: {
    /**
     * Fetches the Query Workspace for the user
     * Payload being passed:
     * @param {Number} userId - The id of the user whose workspace we are retrieving
     */
    async fetchWorkspace ({ commit }, userId) {
      try {
        const queryWorkspace = (await axios.get(`${process.env.VUE_APP_API_URI}/query-pad/query-workspace`, {
          params: {
            userId
          }
        })).data

        commit('setWorkspace', queryWorkspace)
      } catch (error) {
        console.error(`Error while fetching Query Repository: ${error}`)
        throw error
      }
    },

    /**
     * Adds a file to the Query Workspace
     * contents of payload:
     * @param {Object} file - The file object that we are adding the query workspace
     * @param {Number} userId - The id of the user whose workspace we are adding the file to
     * @return {Promise} - Resolves to the added file
     */
    async addQueryWorkspaceFile ({ commit }, { file, userId }) {
      try {
        const { queryName, queryText, savedAt } = file
        const addedFile = (await axios({
          method: 'post',
          url: `${process.env.VUE_APP_API_URI}/query-pad/query-workspace`,
          data: {
            userId,
            queryName,
            queryText,
            savedAt
          }
        })).data

        commit('addFile', addedFile)
        return addedFile
      } catch (error) {
        console.error(`Error while adding to Query Workspace: ${error}`)
        throw error
      }
    },

    /**
     * Updates a file in the Query Workspace
     * Contents of payload:
     * @param {Object} file - The file object with updated properties
     * @param {Number} userId - The id of the user who owns the file
     */
    async updateQueryWorkspaceFile ({ commit }, { file, userId }) {
      try {
        const { id, queryName, queryText } = file
        await axios({
          method: 'patch',
          url: `${process.env.VUE_APP_API_URI}/query-pad/query-workspace`,
          data: {
            userId,
            queryId: id,
            queryName,
            queryText
          }
        })

        commit('updateFile', file)
      } catch (error) {
        console.error(`Error while updating Query Workspace file: ${error}`)
        throw error
      }
    },

    /**
     * Removes a file from the query workspace
     * @param {Number} fileId - The id of the file being removed
     * @param {Number} userId - The id of the user who owns the file
     */
    async removeQueryWorkspaceFile ({ commit }, { fileId, userId }) {
      try {
        await axios({
          method: 'delete',
          url: `${process.env.VUE_APP_API_URI}/query-pad/query-workspace`,
          data: {
            userId,
            queryId: fileId
          }
        })

        commit('removeFile', fileId)
      } catch (error) {
        console.error(`Error while deleting Query Workspace file: ${error}`)
        throw error
      }
    }
  }
}
